import { defineStore } from "pinia";

export const useUserInfoStore = defineStore({
  id: "userInfo",
  state: () => {
    return {
      access_token: null,
      user: null,
    };
  },
  getters: {
    isPst() {
      return this.user?.is_pst_role ?? false;
    },
    isCompanyOwner() {
      return (company_id) => {
        if (this.isPst) {
          return true;
        } else {
          let _isOwner = false;
          const targetCompany = this.user?.companies?.find(
            (company) => company.id == company_id,
          );
          if (targetCompany) {
            _isOwner = targetCompany.is_owner;
          }
          return _isOwner;
        }
      };
    },
    isPartnerOwner() {
      return (partner_id) => {
        if (this.isPst) {
          return true;
        } else {
          let _isOwner = false;
          const targetPartner = this.user?.partners?.find(
            (partner) => partner.id == partner_id,
          );
          if (targetPartner) {
            _isOwner = targetPartner.is_owner;
          }
          return _isOwner;
        }
      };
    },
    isMe() {
      return (user_id) => {
        return this.user !== null && this.user.id === user_id;
      };
    },
    isCompanyAdmin() {
      return (company_id) => {
        // 対象の企業のオーナーは可
        if (this.isCompanyOwner(company_id)) {
          return true;
        }
        return this.hasRole(["admin"], company_id);
      };
    },
    isCompanyViewer() {
      return (company_id) => {
        return this.hasRole(["viewer"], company_id);
      };
    },
    canViewEmployeeAsViewer() {
      return (company_id, department_ids) => {
        if (this.isPst) {
          return true;
        }

        if (!this.isCompanyViewer(company_id)) {
          return false;
        }

        const viewerAccessRestrictions =
          this.getViewerAccessRestrictions(company_id);
        if (viewerAccessRestrictions === null) {
          return false;
        }

        const departments = viewerAccessRestrictions?.departments;
        if (departments === null) {
          return true;
        }

        if (!Array.isArray(departments) || !Array.isArray(department_ids)) {
          return false;
        }

        const accessibleDepartmentIds = departments.map((department) =>
          typeof department === "object" ? department.id : department,
        );
        return department_ids.some((department_id) =>
          accessibleDepartmentIds.includes(department_id),
        );
      };
    },
    getViewerAccessRestrictions() {
      return (company_id) => {
        if (this.user?.viewer_access_restrictions !== undefined) {
          return this.user.viewer_access_restrictions;
        }

        return this.user?.companies?.find(
          (company) => company.id === company_id,
        )?.viewer_access_restrictions;
      };
    },
    isCompanyUser() {
      return (company_id) => {
        if (this.isPst) {
          return true;
        } else {
          let _isUser = false;
          const targetCompany = this.user?.companies?.find(
            (company) => company.id == company_id,
          );
          if (targetCompany) {
            _isUser = true;
          }
          return _isUser;
        }
      };
    },
    hasPartnersOwner() {
      if (this.isPst) {
        return true;
      } else {
        const partnersOwner = this.user?.partners?.filter(
          (partner) => partner.is_owner,
        );
        return partnersOwner.length > 0;
      }
    },
    hasCompaniesOwner() {
      if (this.isPst) {
        return true;
      } else {
        const companiesOwner = this.user?.companies?.filter(
          (company) => company.is_owner,
        );
        return companiesOwner.length > 0;
      }
    },
    hasCompaniesUser() {
      if (this.isPst) {
        return true;
      } else {
        return this.user?.companies?.length;
      }
    },
    hasRole() {
      return (roles, company_id = null) => {
        if (this.isPst) {
          return true;
        } else {
          let _hasRole = false;
          if (company_id !== null) {
            const targetCompany = this.user?.companies?.find(
              (company) => company.id == company_id,
            );
            if (targetCompany) {
              _hasRole = targetCompany.roles.some((role) =>
                roles.includes(role),
              );
            }
          } else {
            // 企業IDの指定がない場合は、対象のロールを持っているかだけの確認
            _hasRole = this.user?.companies?.some((company) =>
              roles.includes(company.roles),
            );
          }
          return _hasRole;
        }
      };
    },
    hasCompanyAdminRole() {
      return this.hasRole(["admin"]);
    },
    hasCompanyViewerRole() {
      return this.hasRole(["viewer"]);
    },
  },
  actions: {
    setAccessToken(token) {
      this.access_token = token;
    },
    setUser(user) {
      this.user = user;
    },
    reset() {
      this.$reset();
    },
  },
  persist: {
    storage: sessionStorage,
  },
});
